import type { ItemOptionPrice } from "@/redux/services/menu/menu.type";

/**
 * The option price row that applies to one placement, per option value id.
 *
 * A dish option is shared by every menu the dish is on, so the API can send
 * several rows per value: this placement's own row (`menuItemId` = this
 * placement), the dish default (`menuItemId` null) and, from older API
 * versions, other menus' rows. Per value we take this placement's row, else
 * the dish default; rows of another menu are never used. Without any
 * `menuItemId` marker (older API), the first row per value wins.
 *
 * The edit form must use the same row to prefill the price and to send its
 * `@id` back, or the price shown for one row is saved into another
 * (SFRESTO-123).
 */
export function pickPlacementPriceRows(
  rows: ItemOptionPrice[] | undefined,
  placementId: string | undefined
): Record<string, ItemOptionPrice> {
  const picked: Record<string, ItemOptionPrice> = {};
  const isOwn: Record<string, boolean> = {};

  (rows ?? []).forEach((row) => {
    const valueId = row.menuItemOptionValue.id;
    const owner = row.menuItemId ?? null;

    if (owner !== null && owner === placementId) {
      if (!isOwn[valueId]) {
        picked[valueId] = row;
        isOwn[valueId] = true;
      }
      return;
    }

    if (owner === null && !picked[valueId]) {
      picked[valueId] = row;
    }
  });

  return picked;
}

/** An option price typed in currency units, as whole cents. */
export function optionPriceToCent(price: number): number {
  return Math.round(Number((Number(price) * 100).toFixed(2)));
}
